import React, { useState } from 'react';
import { Grid, Typography, Divider, Box, Paper, IconButton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import AutocompleteField from 'components/formFields/autocompleteField';
import InputField from 'components/formFields/InputField';
import CheckboxField from 'components/formFields/checkBoxField';
import { InfoModal } from 'components/generics/InfoModal';

export const AjustesGlobalesIaForm = ({ formField }: any) => {
    const {
        defaultChatServiceId,
        defaultImageServiceId,
        defaultAudioServiceId,
        defaultEmbeddingServiceId,
        defaultOcrServiceId,
        creditsPerUsd,
        defaultTemperature,
        defaultTopP,
        defaultMaxTokens,
        enableStreaming,
        enableMemory,
        enableCache,
        enableTools,
        enableLogs,
        allowBringYourOwnKey
    } = formField;

    const { applicationsListSelect } = useSelector((state: any) => state.servicesIaGlobalConfig);
    const [activeModal, setActiveModal] = useState<string | null>(null);

    const getModalContent = () => {
        switch (activeModal) {
            case 'defaultChatServiceId': return { title: 'Servicio Chat', description: 'Servicio de IA por defecto utilizado para tareas conversacionales y generación de texto.', infoText: 'Ejemplo: Si eliges Gemini 1.5 Pro o GPT-4, todas las respuestas de texto de la aplicación usarán este modelo a menos que se especifique otro.' };
            case 'defaultImageServiceId': return { title: 'Servicio Imagen', description: 'Servicio de IA por defecto para la generación, análisis y edición de imágenes.', infoText: 'Ejemplo: Podrías seleccionar DALL-E 3 para generar avatares y miniaturas automáticamente.' };
            case 'defaultAudioServiceId': return { title: 'Servicio Audio', description: 'Servicio de IA por defecto para procesamiento de audio (transcripción y generación de voz).', infoText: 'Ejemplo: Whisper de OpenAI para convertir mensajes de voz de usuarios en texto.' };
            case 'defaultEmbeddingServiceId': return { title: 'Servicio Embedding', description: 'Servicio por defecto para la vectorización de datos, esencial para búsquedas semánticas y bases de conocimiento.', infoText: 'Ejemplo: Modelos como text-embedding-3-small se usan para comparar qué tan similares son dos documentos de políticas del hotel.' };
            case 'defaultOcrServiceId': return { title: 'Servicio OCR', description: 'Servicio por defecto para extraer texto estructurado de documentos e imágenes (Reconocimiento Óptico de Caracteres).', infoText: 'Ejemplo: Al subir la foto de una factura, se usa OCR para extraer automáticamente el número de comprobante y el total.' };
            case 'creditsPerUsd': return { title: 'Créditos por USD', description: 'Tasa de conversión que define cuántos créditos internos obtiene el usuario por cada dólar.', infoText: 'Ejemplo: Si configuras 100, un recargo de $5 USD agregará 500 créditos al balance del cliente.' };
            case 'defaultTemperature': return { title: 'Temperatura Base', description: 'Controla el nivel de creatividad de la respuesta. Un valor más cercano a 0 es más determinista y conservador, mientras que hacia 2 es más aleatorio.', infoText: 'Ejemplo: Usa 0.2 para respuestas técnicas exactas (como políticas de devolución) y 0.8 para redacción creativa (como sugerir ideas de marketing).' };
            case 'defaultTopP': return { title: 'Top-P Base', description: 'Otra forma de controlar la aleatoriedad, conocida como muestreo de núcleo. Limita el modelo a elegir entre el top % de palabras probables.', infoText: 'Ejemplo: 0.9 significa que el modelo solo considerará el 90% de las opciones más probables, ignorando palabras muy inusuales.' };
            case 'defaultMaxTokens': return { title: 'Tokens Máximos Base', description: 'Límite máximo por defecto de la cantidad de tokens (palabras o fragmentos) que el modelo puede devolver.', infoText: 'Ejemplo: Si pones 500, garantizas que las respuestas nunca sean excesivamente largas, protegiendo así tus costos de API.' };
            case 'enableStreaming': return { title: 'Streaming', description: 'Habilita la respuesta en tiempo real ("efecto máquina de escribir") en toda la aplicación.', infoText: 'Ejemplo: En lugar de esperar 5 segundos viendo "Cargando...", el usuario verá aparecer el texto palabra por palabra inmediatamente.' };
            case 'enableMemory': return { title: 'Memoria Compartida', description: 'Permite que el sistema recuerde contextos de conversaciones anteriores para mejorar la continuidad.', infoText: 'Ejemplo: Si un usuario preguntó por los restaurantes del hotel ayer, la IA recordará esto si pregunta "cuál era el de mariscos".' };
            case 'enableCache': return { title: 'Caché Global', description: 'Almacena respuestas a preguntas frecuentes para ahorrar costos y reducir la latencia.', infoText: 'Ejemplo: Si 10 personas preguntan "¿A qué hora es el check-in?", la primera petición va a OpenAI, pero las otras 9 se responden desde la caché de forma gratuita e instantánea.' };
            case 'enableTools': return { title: 'Llamadas a Funciones', description: 'Permite que los agentes de IA ejecuten funciones internas de la plataforma.', infoText: 'Ejemplo: El bot podría conectarse al sistema de reservaciones para ejecutar la función `buscarHabitacion(fecha)` en lugar de solo dar texto.' };
            case 'enableLogs': return { title: 'Registro Completo', description: 'Almacena detalladamente todos los prompts y completions para auditoría.', infoText: 'Ejemplo: Podrás revisar exactamente qué le preguntó el huésped a la IA y qué le respondió si surge una queja en recepción.' };
            case 'allowBringYourOwnKey': return { title: 'Llevar su Propia Clave (BYOK)', description: 'Permite a los usuarios avanzados utilizar su propia API Key (ej. de OpenAI) en lugar de consumir los créditos del sistema.', infoText: 'Ejemplo: Empresas con contratos propios de OpenAI ingresan su llave y tú ya no asumes sus costos operativos de IA.' };
            default: return { title: '', description: '', infoText: '' };
        }
    };
    
    const modalContent = getModalContent();

    const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                {field}
            </Box>
            <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
                <InfoOutlined />
            </IconButton>
        </Box>
    );

    const renderCheckboxWithInfo = (field: React.ReactNode, modalKey: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {field}
            <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
                <InfoOutlined />
            </IconButton>
        </Box>
    );

    return (
        <>
            <Grid container spacing={3}> 
                {/* Sección: Configuración de Servicios por Defecto */}
                <Grid size={{ xs:12 }}>
                    <Box mb={2}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>Servicios por Defecto</Typography>
                        <Typography variant="body2" color="textSecondary">Selecciona qué servicio se usará por defecto para cada tipo de tarea.</Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    {renderFieldWithInfo(
                        <AutocompleteField
                            name={defaultChatServiceId.name}
                            label={defaultChatServiceId.label}
                            options={(applicationsListSelect || []).filter((app: any) => app.serviceType === 'CHAT')}
                            fullWidth
                        />, 'defaultChatServiceId'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    {renderFieldWithInfo(
                        <AutocompleteField
                            name={defaultImageServiceId.name}
                            label={defaultImageServiceId.label}
                            options={(applicationsListSelect || []).filter((app: any) => app.serviceType === 'IMAGE')}
                            fullWidth
                        />, 'defaultImageServiceId'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    {renderFieldWithInfo(
                        <AutocompleteField
                            name={defaultAudioServiceId.name}
                            label={defaultAudioServiceId.label}
                            options={(applicationsListSelect || []).filter((app: any) => app.serviceType === 'AUDIO')}
                            fullWidth
                        />, 'defaultAudioServiceId'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 6}}>
                    {renderFieldWithInfo(
                        <AutocompleteField
                            name={defaultEmbeddingServiceId.name}
                            label={defaultEmbeddingServiceId.label}
                            options={(applicationsListSelect || []).filter((app: any) => app.serviceType === 'EMBEDDING')}
                            fullWidth
                        />, 'defaultEmbeddingServiceId'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 6}}>
                    {renderFieldWithInfo(
                        <AutocompleteField
                            name={defaultOcrServiceId.name}
                            label={defaultOcrServiceId.label}
                            options={(applicationsListSelect || []).filter((app: any) => app.serviceType === 'OCR')}
                            fullWidth
                        />, 'defaultOcrServiceId'
                    )}
                </Grid>

                {/* Sección: Configuración de Tokens y Parámetros */}
                <Grid size={{xs: 12}} sx={{ mt: 2 }}>
                    <Box mb={2}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>Tokens y Parámetros</Typography>
                        <Typography variant="body2" color="textSecondary">Ajusta los valores predeterminados para la generación de respuestas.</Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    {renderFieldWithInfo(
                        <InputField
                            name={creditsPerUsd.name}
                            label={creditsPerUsd.label}
                            type="number"
                            fullWidth
                        />, 'creditsPerUsd'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    {renderFieldWithInfo(
                        <InputField
                            name={defaultTemperature.name}
                            label={defaultTemperature.label}
                            type="number"
                            inputProps={{ step: "0.01", min: "0", max: "2" }}
                            fullWidth
                        />, 'defaultTemperature'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    {renderFieldWithInfo(
                        <InputField
                            name={defaultTopP.name}
                            label={defaultTopP.label}
                            type="number"
                            inputProps={{ step: "0.01", min: "0", max: "1" }}
                            fullWidth
                        />, 'defaultTopP'
                    )}
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                    {renderFieldWithInfo(
                        <InputField
                            name={defaultMaxTokens.name}
                            label={defaultMaxTokens.label}
                            type="number"
                            fullWidth
                        />, 'defaultMaxTokens'
                    )}
                </Grid>

                {/* Sección: Capacidades y Opciones Avanzadas */}
                <Grid size={{xs: 12}} sx={{ mt: 2 }}>
                    <Box mb={2}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>Capacidades y Opciones Avanzadas</Typography>
                        <Typography variant="body2" color="textSecondary">Activa o desactiva características globales de la plataforma.</Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid size={{xs: 12}}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Grid container spacing={2}>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={enableStreaming.name}
                                        label={enableStreaming.label}
                                    />, 'enableStreaming'
                                )}
                            </Grid>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={enableMemory.name}
                                        label={enableMemory.label}
                                    />, 'enableMemory'
                                )}
                            </Grid>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={enableCache.name}
                                        label={enableCache.label}
                                    />, 'enableCache'
                                )}
                            </Grid>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={enableTools.name}
                                        label={enableTools.label}
                                    />, 'enableTools'
                                )}
                            </Grid>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={enableLogs.name}
                                        label={enableLogs.label}
                                    />, 'enableLogs'
                                )}
                            </Grid>
                            <Grid size={{xs: 12, sm: 6, md: 4}}>
                                {renderCheckboxWithInfo(
                                    <CheckboxField
                                        name={allowBringYourOwnKey.name}
                                        label={allowBringYourOwnKey.label}
                                    />, 'allowBringYourOwnKey'
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            
            <InfoModal
                open={activeModal !== null}
                setOpen={(val: boolean) => { if (!val) setActiveModal(null); }}
                title={modalContent.title}
                description={modalContent.description}
                infoText={modalContent.infoText}
                icon="Info"
            />
        </>
    );
};
